import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { driversApi } from '../../api/drivers';
import { vehiclesApi } from '../../api/vehicles';
import { tripsApi } from '../../api/trips';
import toast from 'react-hot-toast';

const AssignmentModal = ({ isOpen, onClose, tripId, tripData, isReassign = false }) => {
  const [formData, setFormData] = useState({
    driver_id: '',
    vehicle_id: '',
    clMotivoReasignacion: ''
  });
  const [errors, setErrors] = useState({});
  const queryClient = useQueryClient();

  // Reassignment reasons
  const reassignmentReasons = [
    { value: 1, label: 'Por mejorar el tiempo de contacto' },
    { value: 2, label: 'Por logistica del proveedor' }
  ];

  // Fetch drivers and vehicles
  const { data: driversData } = useQuery({
    queryKey: ['drivers-active'],
    queryFn: () => driversApi.getAll({ status: 'activo' }),
    enabled: isOpen
  });

  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles-active'],
    queryFn: () => vehiclesApi.getAll({ status: 'activo' }),
    enabled: isOpen
  });

  // Assignment mutation
  const assignmentMutation = useMutation({
    mutationFn: (data) => {
      if (isReassign) {
        return tripsApi.reassign(tripId, data);
      } else {
        return tripsApi.assign(tripId, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['servicios'] });
      toast.success(isReassign ? 'Reasignación creada exitosamente' : 'Asignación creada exitosamente');
      onClose();
    },
    onError: (error) => {
      //const message = error.response?.data?.message || (isReassign ? 'Error al reasignar' : 'Error al crear asignación');
      //toast.error(message);
    }
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        driver_id: '',
        vehicle_id: '',
        clMotivoReasignacion: ''
      });
      setErrors({});
    }
  }, [isOpen, tripData]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!formData.driver_id) newErrors.driver_id = 'Selecciona un chófer';
    if (!formData.vehicle_id) newErrors.vehicle_id = 'Selecciona un vehículo';
    if (isReassign && !formData.clMotivoReasignacion) {
      newErrors.clMotivoReasignacion = 'Selecciona un motivo de reasignación';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    assignmentMutation.mutate(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (!isOpen) return null;

  const drivers = Array.isArray(driversData) ? driversData : (driversData?.data || []);
  const vehicles = Array.isArray(vehiclesData) ? vehiclesData : (vehiclesData?.data || []);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="modal-card" style={{
        background: 'var(--panel-bg)',
        borderRadius: '12px',
        padding: '24px',
        minWidth: '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {/* Header */}
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            {isReassign ? 'Reasignar Viaje' : 'Asignar Viaje'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#eaeaea',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              fontSize: '20px'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Trip Info */}
        {tripData && (
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
              Información del Viaje
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
              <div>
                <strong>ID IKE:</strong> {tripData.ike_id || 'N/A'}
              </div>
              <div>
                <strong>Fecha y Hora de Recogida:</strong> {formatDateTime(tripData.fecha_hora)}
              </div>
              <div>
                <strong>Origen:</strong> {tripData.origen?.completo || [tripData.origen_calle, tripData.origen_colonia, tripData.origen_municipio].filter(Boolean).join(', ') || 'N/A'}
              </div>
              <div>
                <strong>Destino:</strong> {(() => {
                  const d = tripData.destino;
                  // Handle nested destination object
                  if (d) {
                    if (d.terminal) {
                      const terminals = { 1: 'AICM T1', 2: 'AICM T2', 3: 'AIFA' };
                      const name = d.terminal_nombre || terminals[d.terminal];
                      const type = typeof d.tipo_vuelo === 'number'
                        ? (d.tipo_vuelo === 1 ? 'Nacional' : 'Internacional')
                        : d.tipo_vuelo;
                      return name ? (type ? `${name} - ${type}` : name) : 'Terminal';
                    }
                    // Not a terminal: show address fields
                    return [d.calle, d.colonia, d.municipio].filter(Boolean).join(', ') || d.completo || 'N/A';
                  }

                  // Handle flat destination fields
                  const terminals = { 1: 'AICM T1', 2: 'AICM T2', 3: 'AIFA' };
                  if (tripData.destino_terminal) {
                    const label = terminals[tripData.destino_terminal];
                    const type = tripData.destino_tipo_vuelo === 1 ? 'Nacional' : (tripData.destino_tipo_vuelo === 2 ? 'Internacional' : '');
                    return label ? `${label} - ${type}` : 'N/A';
                  }

                  return [tripData.destino_calle, tripData.destino_colonia, tripData.destino_municipio].filter(Boolean).join(', ') || 'N/A';
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Driver Selection */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: errors.driver_id ? '#ef4444' : '#eaeaea'
              }}>
                Chófer *
              </label>
              <select
                name="driver_id"
                value={formData.driver_id}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${errors.driver_id ? '#ef4444' : 'rgba(255,255,255,0.2)'}`,
                  background: 'rgba(255,255,255,0.05)',
                  color: '#eaeaea',
                  fontSize: '14px'
                }}
              >
                <option value="">Selecciona un chófer</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {[driver.nombre, driver.apellido_paterno, driver.apellido_materno].filter(Boolean).join(' ')} - {driver.licencia}
                  </option>
                ))}
              </select>
              {errors.driver_id && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                  {errors.driver_id}
                </div>
              )}
            </div>

            {/* Vehicle Selection */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: errors.vehicle_id ? '#ef4444' : '#eaeaea'
              }}>
                Vehículo *
              </label>
              <select
                name="vehicle_id"
                value={formData.vehicle_id}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${errors.vehicle_id ? '#ef4444' : 'rgba(255,255,255,0.2)'}`,
                  background: 'rgba(255,255,255,0.05)',
                  color: '#eaeaea',
                  fontSize: '14px'
                }}
              >
                <option value="">Selecciona un vehículo</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {[vehicle.marca, vehicle.modelo, vehicle.color].filter(Boolean).join(' ')} - {vehicle.placas}
                  </option>
                ))}
              </select>
              {errors.vehicle_id && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                  {errors.vehicle_id}
                </div>
              )}
            </div>

            {/* Reassignment Reason - Only show when reassigning */}
            {isReassign && (
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: errors.clMotivoReasignacion ? '#ef4444' : '#eaeaea'
                }}>
                  Motivo de Reasignación *
                </label>
                <select
                  name="clMotivoReasignacion"
                  value={formData.clMotivoReasignacion}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${errors.clMotivoReasignacion ? '#ef4444' : 'rgba(255,255,255,0.2)'}`,
                    background: 'rgba(255,255,255,0.05)',
                    color: '#eaeaea',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Selecciona un motivo</option>
                  {reassignmentReasons.map(reason => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
                {errors.clMotivoReasignacion && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    {errors.clMotivoReasignacion}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'transparent',
                color: '#eaeaea',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={assignmentMutation.isPending}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                background: assignmentMutation.isPending ? 'rgba(139, 173, 157, 0.5)' : 'var(--secondary)',
                color: '#000',
                cursor: assignmentMutation.isPending ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {assignmentMutation.isPending
                ? (isReassign ? 'Reasignando...' : 'Asignando...')
                : (isReassign ? 'Reasignar Viaje' : 'Asignar Viaje')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignmentModal;
